import React, { ActionDispatch, FC, JSX, useContext, useEffect, useMemo, useRef, useState } from "react";

import { NetworkAssessmentAnalysisType, ProjectionProviderType, ProviderNetworkType } from "@app/projects/enumerators";
import { AdequacyDetail } from "@app/projects/network-assessment/adequacy";
import ProjectService from "@app/projects/projectService";
import { ProjectionProvider } from "@app/projects/results/projection-provider";
import { ServicingProvider } from "@app/projects/results/servicing-provider";
import { QESAlert, QESButton } from "@app/shared/qes-standards";
import { Accordion, AccordionContainer, LoadingSpinner } from "@app/shared";
import { ColumnConfig, ColumnGroup, Waffles } from "@app/shared/waffles";
import { CountyProjectContext } from "@app/projects";
import { getServicingGranularityHeader } from "@app/projects/network-assessment/adequacy/utility";
import { Features } from "@app/feature-flags";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import { Roles, UserInfo } from "@app/user";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import {
  PreviewWaffleToppings,
  simulationPreviewWaffleToppings,
  zipSimulationPreviewWaffleToppings
} from "@app/projects/servicing-provider/SimulationPreviewHelpers";
import {
  ProjectionProviderDetailsAction,
  ProjectionProviderDetailsActionType,
  ProjectionProviderDetailsForm,
  ProjectionProviderStatus
} from "@app/projects/projection-provider/projectionProviderDetailsReducer";
import ProjectionProviderByEnum from "./projectionProviderByEnum";
import { ZipClassAggregateResultDisplayModel, ZipCodeDisplayModel } from "@app/projects/zip-projects/interfaces";
import { ZipProjectContext } from "../zip-projects/ZipProjectContext";

interface VisibilityProps {
  showAvgDist: boolean;
  showAvgTime: boolean;
  showNetNew: boolean;
  showWithTimeAndDistanceColumns: boolean;
}

type SimulateProps = {
  processingContent: JSX.Element;
  projectionProviderType: ProjectionProviderType;
  providerNetworkType: ProviderNetworkType;
  regionSetId: number;
  selectedProviders: ServicingProvider[];
  specialtyGroupCode: string;
  visibility: VisibilityProps;
  user: UserInfo;
  formState: ProjectionProviderDetailsForm;
  formDispatch: ActionDispatch<[action: ProjectionProviderDetailsAction]>;
  isZipProject: boolean;
};

const SimulateProviderTargeting: FC<React.PropsWithChildren<SimulateProps>> = (props: SimulateProps) => {
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState<AdequacyDetail[]>([]);
  const [zipCodePreview, setZipCodePreview] = useState<ZipCodeDisplayModel[] | ZipClassAggregateResultDisplayModel[]>([]);
  const [zipClassPreview, setZipClassPreview] = useState<ZipClassAggregateResultDisplayModel[]>([]);
  const projectService = useRef<ProjectService>(new ProjectService()).current;
  const [error, setError] = useState<JSX.Element>(null);
  const projectContext = useContext(CountyProjectContext);
  const zipProjectContext = useContext(ZipProjectContext);

  const cmsCreditIsApplying = props.isZipProject ? false : projectContext.cmsCreditIsApplying;
  const runConfiguration = props.isZipProject ? zipProjectContext.state.runConfiguration : projectContext.runConfiguration;
  const networkAssessmentAnalysisType = props.isZipProject ? NetworkAssessmentAnalysisType.ServiceArea : projectContext.networkAssessmentAnalysisType;
  const project = props.isZipProject ? zipProjectContext.state.project : projectContext.project;
  const templateMetadata = props.isZipProject ? zipProjectContext.state.templateMetadata : projectContext.templateMetadata;

  const adequacyData = props.isZipProject ? null : projectContext.adequacyResult.details;
  const zipCodeData = !props.isZipProject ? null : zipProjectContext.state.zipDetails;
  const zipClassData = !props.isZipProject ? null : zipProjectContext.state.zipClassDrillInDetails;

  const { isProjectedTermed, isTermed, projectRunConfigurationType } = runConfiguration;
  const [targetProvidersCount, setTargetProvidersCount] = useState<number>(null);
  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;

  const runProjection = () => {
    if (props.user.roles.includes(Roles.TIN_VIEWER) && featureFlags.has(Features.TARGET_EXCLUDE_BY_NAME)) {
      validateProviderTypes(props.selectedProviders);
    }

    if (!props.isZipProject) {
      projectService.simulateProjection(
        project.projectId,
        props.regionSetId,
        props.specialtyGroupCode,
        {
          allLocations: props.formState.allLocations,
          allSpecialties: props.formState.allSpecialties,
          projectionBy: props.formState.identifier.includes("npitin") ? ProjectionProviderByEnum.NPIAndTIN
            : props.formState.identifier.includes("npi") ? ProjectionProviderByEnum.NPI
              : props.formState.identifier.includes("tin") ? ProjectionProviderByEnum.TIN
                : ProjectionProviderByEnum.Name,
          projectionType: props.projectionProviderType,
          providerIds: props.selectedProviders.map((sp) => sp.providerId),
          providerNetworkType: props.providerNetworkType,
          //if the selected providers are projection providers then pass the projection provider ids to simulate the deletion of them
          removedProjectionProviderProjectIds: props.selectedProviders.map((sp) => (sp as ProjectionProvider).projectionProviderProjectId).filter((projectionProviderProjectId) => !!projectionProviderProjectId)
        },
        networkAssessmentAnalysisType,
        isProjectedTermed,
        isTermed,
        projectRunConfigurationType,
      ).then((result) => {
        setPreview([result]);
        setTargetProvidersCount(result.targetProvidersCount - props.selectedProviders.length);
        setLoading(false);
        props.formDispatch({ type: ProjectionProviderDetailsActionType.RunPreview })
      }, () => {
        setError(<QESAlert type="danger">Something went wrong on our end.</QESAlert>);
      });
    }
    else
    {
      //TODO: Call zip code targeting simulation code when implemented
      setZipCodePreview(zipCodeData);
      setZipClassPreview(zipClassData);
      setLoading(false);
      setTargetProvidersCount(0);
    }
    
  }

  useEffect(() => {
    if (props.formState.status !== ProjectionProviderStatus.Ready) {
      return;
    }
    runProjection();
  }, [props.formState.status]);

  const handleReloadRequest = () => {
    setLoading(true);
    setPreview([]);
    runProjection();
  }

  const validateProviderTypes = (selectedProviders: ServicingProvider[]): void => {
    let hasNPIOnly = false;
    let hasTINOnly = false;
    for (let i = 0; i < selectedProviders.length; i++) {
      if (selectedProviders[i].npi !== undefined && selectedProviders[i].tin === undefined) {
        hasNPIOnly = true;
      } else if (selectedProviders[i].tin !== undefined && selectedProviders[i].npi === undefined) {
        hasTINOnly = true;
      }
    }
    if (hasNPIOnly && hasTINOnly) {
      // Throw an error if there are providers with only NPIs and providers with only TINs.
      setError(<QESAlert type="danger">All selected providers must have NPIs or all selected providers must have
        TINs.</QESAlert>);
    }
  };

  const projectedWaffleToppings: PreviewWaffleToppings[] = useMemo(() => {
    if (!props.isZipProject)
      return simulationPreviewWaffleToppings(preview, adequacyData, cmsCreditIsApplying);
    else
      return zipSimulationPreviewWaffleToppings(zipCodePreview, zipCodeData, zipProjectContext.state.calculationMethod);
  }, [preview, adequacyData, zipCodePreview, zipCodeData]);

  const projectedAggregateWaffleToppings: PreviewWaffleToppings[] = useMemo(() => {
    return zipSimulationPreviewWaffleToppings(zipClassPreview, zipClassData, zipProjectContext.state.calculationMethod);
  }, [zipClassPreview, zipClassData]);

  const columnConfig: ColumnConfig = useMemo(() => {
    const showAvgDist = runConfiguration.showDistance && project.showAverageDistance;
    const showAvgTime = runConfiguration.showTime && project.showAverageTime;
    const showWithTimeAndDistanceColumns = runConfiguration.showWithTimeAndDistanceColumns;
    const showNextClosestProvider = templateMetadata.closestProviderCount > 1;

    return {
      accessAvgDist: {
        displayText: showNextClosestProvider ? "Avg Dist 1" : "Avg Dist",
        hidden: !showAvgDist,
        noSort: true,
        testId: "access-avg-dist",
      },
      accessAvgDist2: {
        displayText: "Avg Dist 2",
        hidden: !showAvgDist || !(showNextClosestProvider) || !props.isZipProject,
        noSort: true,
        testId: "access-avg-dist",
      },
      accessAvgTime: {
        displayText: showNextClosestProvider ? "Avg Time 1" : "Avg Time",
        hidden: !showAvgTime,
        noSort: true,
        testId: "access-avg-time",
      },
      accessAvgTime2: {
        displayText: "Avg Time 2",
        hidden: !showAvgTime || !(showNextClosestProvider) || !props.isZipProject,
        noSort: true,
        testId: "access-avg-time",
      },
      accessMet: {
        displayText: "Met",
        hidden: showWithTimeAndDistanceColumns || props.isZipProject,
        noSort: true,
        testId: "access-met",
      },
      accessMetDist: {
        displayText: "Met Dist",
        hidden: !showWithTimeAndDistanceColumns,
        noSort: true,
        testId: "access-met-dist",
      },
      accessMetTime: {
        displayText: "Met Time",
        hidden: !showWithTimeAndDistanceColumns,
        noSort: true,
        testId: "access-met-time",
      },
      accessWith: {
        displayText: "With %",
        noSort: true,
        testId: "access-with-percent",
      },
      accessWithout: {
        displayText: "Without %",
        noSort: true,
        testId: "access-without-percent",
      },
      marketAccess: {
        displayText: "Access %",
        hidden: !runConfiguration.marketProviderSetId,
        noSort: true,
        testId: "access-percent",
      },
      marketDistinct: {
        displayText: "Servicing",
        hidden: !runConfiguration.marketProviderSetId,
        noSort: true,
        testId: "market-distinct",
      },
      marketPercentage: {
        displayText: "Market %",
        hidden: !runConfiguration.marketProviderSetId,
        noSort: true,
        testId: "market-percentage",
      },
      marketProviderCount: {
        displayText: getServicingGranularityHeader(templateMetadata.servicingAnalysisGranularity),
        hidden: !runConfiguration.marketProviderSetId || props.isZipProject,
        noSort: true,
        testId: "market-provider-count",
      },
      overallGapAge: {
        displayText: "Gap Age",
        hidden: !project.includeGapAging || props.isZipProject,
        noSort: true,
        testId: "overall-gap-age",
      },
      overallMet: {
        displayText: "Met",
        noSort: true,
        testId: "overall-met",
      },
      servicingDistinct: {
        displayText: "Servicing",
        hidden: props.isZipProject,
        noSort: true,
        testId: "servicing-distinct",
      },
      servicingMet: {
        displayText: "Met",
        hidden: props.isZipProject,
        noSort: true,
        testId: "servicing-met",
      },
      servicingProviderCount: {
        displayText: getServicingGranularityHeader(templateMetadata.servicingAnalysisGranularity),
        hidden: props.isZipProject,
        noSort: true,
        testId: "servicing-provider-count",
      },
      servicingRequired: {
        displayText: "Required",
        hidden: props.isZipProject,
        noSort: true,
        testId: "servicing-required",
      },
    };
  }, [runConfiguration, project, templateMetadata]);

  const columnGroups: Array<ColumnGroup<PreviewWaffleToppings>> = [
    {
      children: ["accessWith", "accessWithout", "accessAvgDist", "accessAvgTime", "accessAvgDist2", "accessAvgTime2", "accessMetDist", "accessMetTime", "accessMet",],
      hideKey: false,
      key: "Access Requirements",
      testId: "group-access-requirements",
    },
    {
      children: ["servicingRequired", "servicingProviderCount", "servicingDistinct", "servicingMet"],
      key: "Servicing Provider Requirements",
      testId: "group-servicing-requirements",
    },
    {
      children: ["overallMet", "overallGapAge"],
      key: "Overall",
      testId: "group-overall",
    },
    {
      children: ["marketAccess", "marketProviderCount", "marketDistinct", "marketPercentage"],
      key: "Market Analysis",
      testId: "group-market-analysis",
    },
  ];

  const renderLoadedContent = (selectedProviders: ServicingProvider[]) => {
    return (
      <>
        <div className="mb-3">
          <p className="mb-0">The top row is what this {props.isZipProject ? "zip code/specialty" : "county/specialty"} combination looks like without targeting.</p>
          {targetProvidersCount == 0 &&
            <p>The bottom row is what it looks like with the targeted providers listed below.</p>}
          {targetProvidersCount > 0 &&
            <QESAlert type="info" persist={true}>
              <div>
                The bottom row is what your adequacy results would look like if the targeted provider(s) listed below
                and {targetProvidersCount} other provider(s) who have been targeted previously were added to your
                network. For full visibility for targeted providers, we recommend running the following reports:
                <ul>
                  <li>Prospect Targeted Provider Report</li>
                  <li>Market Targeted Provider Report</li>
                  {props.visibility.showNetNew && !props.isZipProject && <li>Net New Targeted Provider Report</li>}
                </ul>
              </div>
            </QESAlert>}
          {props.isZipProject && <h4 className="header-s" >Impact Results By Zip Code</h4> }
          {renderTable(projectedWaffleToppings)}
          {props.isZipProject && <h4 className="header-s" style={{ marginTop: "0.5rem" }}>Impact Results By Classification</h4>}
          {props.isZipProject && renderTable(projectedAggregateWaffleToppings)}
        </div>
        <div className="mt-3">
          <QESButton qesType="secondary" qesTestId="simulate-terminations__reload_simulation"
                     onClick={handleReloadRequest} disabled={props.formState.disableRerunPreview}>Rerun Target Preview</QESButton>
        </div>
        <div className="mt-3 mb-5">
          <AccordionContainer>
            <Accordion
              title={<>{<QesIcon isDecorative iconName={QesIcons.FontAwesome.UserMd} width="11" height="14" classNames="align-baseline mr-1" svgStyle={{ position: "relative", top: "1px" }}/>}Selected Providers</>}
              defaultOpen={true}
              testId="simulate-provider-targeting__selected-providers-accordion"
            >
              <ul>
                {selectedProviders.map((provider) => <li key={provider.providerId}>
                  {provider.name} | {provider.npi ? provider.npi + " | " : ""}{provider.address} {provider.city}, {provider.state} {provider.zip}
                </li>)}
              </ul>
            </Accordion>
          </AccordionContainer>
        </div>
      </>
    );
  };

  const renderTable = (tableData: PreviewWaffleToppings[]) => {
    // const data = records.concat(preview);
    // this is a watered down AdequacyDetails
    return (
      <Waffles<PreviewWaffleToppings>
        columns={columnConfig}
        columnGroups={columnGroups}
        rowDecorator={rowDecorator}
        loading={loading}
        data={tableData}
        testId="simulate-provider-targeting"
      />
    );
  };

  return (
    <div>
      <div className="row">
        <div className="col-12">
          {error ? error :
            !!preview ? renderLoadedContent(props.selectedProviders) : <LoadingSpinner />}
        </div>
      </div>
      {props.processingContent}
    </div>
  );
};

const rowDecorator = (record: PreviewWaffleToppings) => {
  if (record.overallMet === "Y") {
    return `success`;
  } else if (record.accessMet === "Y" || record.servicingMet === "Y") {
    return `warning`;
  } else {
    return `danger`;
  }
};

export { SimulateProviderTargeting };
